import { calculateSimilarityScores } from "./tfidf.js";
import {
  chooseAnchoredHit,
  chooseAnchoredOccurrence,
  orderedFromAnchor,
} from "./anchorOccurrence.js";
import { figureHits, figureRegex, rankFigureHits } from "./figureMatch.js";

const sentenceSegmenter = new Intl.Segmenter("en", { granularity: "sentence" });

// Splits highlight context into the sentences the fuzzy matcher scores lines
// against. Uses the platform segmenter rather than an NLP library because it
// knows abbreviations and decimals ("2.0% p.a.", "3.5x") are not sentence ends
// and costs nothing to ship. The newline split is redundant — ICU already
// breaks on one — but states "a line is a segment" without relying on that.
// Blanks are dropped because an empty query scores every line the same and
// would highlight an arbitrary one.
export const splitIntoSentences = (text) =>
  `${text}`
    .split("\n")
    .flatMap((line) =>
      [...sentenceSegmenter.segment(line)].map((s) => s.segment)
    )
    .map((s) => s.trim())
    .filter(Boolean);

const justHighlightNodes = (nodes, level = 100, preventScroll = false) => {
  nodes.forEach((node) => node.classList.add("fp-dark-highlight"));
  if (nodes.length && !preventScroll) scrollToNode(nodes[0]);
};

const scrollToNode = (node) =>
  node.scrollIntoView({ behavior: "smooth", block: "center" });

const highlightSimilarLines = (
  text,
  lines,
  styleTopNodeMap,
  numberOfLinesToHighlight = 3,
  removeGradient = false,
  // Hard cap on highlighted lines. When set, the extension loop below is
  // disabled: extending until the highlighted length covers the query is
  // right for a PDF's short spans, but where a "line" is a whole paragraph
  // (the interaction note viewer, #11496) it tints blocks all over the page.
  maxLines = undefined
) => {
  const tfidfScores = calculateSimilarityScores(text, lines);
  let i = 0;
  for (const top in styleTopNodeMap) {
    styleTopNodeMap[top].tfidfSimilarity = tfidfScores[i++];
  }

  const sortedLines = Object.values(styleTopNodeMap)
    .sort((a, b) => b.tfidfSimilarity - a.tfidfSimilarity)
    .map((line) => ({
      ...line,
      lineText: line.nodes.map((n) => n.textContent).join(""),
    }));

  const totalLines = sortedLines.length;
  let linesToHighlight = Math.min(
    totalLines,
    maxLines ?? numberOfLinesToHighlight
  );
  const origLen = text.length;
  let lenSoFar = 0;

  for (let i = 0; i < linesToHighlight; i++) {
    const { nodes, lineText } = sortedLines[i];
    lenSoFar += lineText.length;
    const level = removeGradient
      ? 100
      : ((linesToHighlight - i) * 100) / linesToHighlight;
    justHighlightNodes(nodes, level, i !== 0);
    if (
      maxLines === undefined &&
      lenSoFar < origLen &&
      i >= linesToHighlight - 1
    )
      linesToHighlight = Math.min(linesToHighlight + 1, totalLines);
  }
};

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractFirstDigits(str, roundToNearest = 5) {
  const m = str.match(/\d+/);
  const raw = m ? m[0] : str;
  let num = parseInt(raw, 10);
  return isNaN(num)
    ? str
    : roundToNearest
      ? Math.round(num / roundToNearest) * roundToNearest
      : num;
}

const getNodeTopPos = (node, roundToNearest = 5) =>
  node?.style?.top
    ? extractFirstDigits(node.style.top, roundToNearest)
    : node?.parentNode
      ? getNodeTopPos(node.parentNode, roundToNearest)
      : "";

function enumerateSpaceVariants(str) {
  const results = [];
  const len = str.length;
  const max = 1 << (len - 1);
  for (let mask = 0; mask < max; mask++) {
    let s = "";
    for (let i = 0; i < len; i++) {
      s += str[i];
      if (i < len - 1 && mask & (1 << i)) {
        s += " ";
      }
    }
    results.push(s);
  }
  return results;
}

const applyFuzzyHighlight2 = (
  textNodes,
  textToMatchP1,
  textToMatchP2,
  numberOfLinesToHighlight = 1,
  removeGradient = true,
  maxLines = undefined
) => {
  const allTopPos = textNodes.map((n) => getNodeTopPos(n, 0));
  const uniqueTopPos = [...new Set(allTopPos)];
  const roundToNearest = parseInt(uniqueTopPos.length / 50);

  let styleTopNodeMap = {};
  for (const n of textNodes) {
    const key = getNodeTopPos(n, roundToNearest);
    (styleTopNodeMap[key] ??= { nodes: [] }).nodes.push(n);
  }

  if (uniqueTopPos.length < 3) {
    styleTopNodeMap = {};
    textNodes.forEach((n, i) => (styleTopNodeMap[i] = { nodes: [n] }));
  }

  const lines = Object.values(styleTopNodeMap).map((l) =>
    l.nodes.map((n) => n.textContent).join("")
  );

  if (textToMatchP1) {
    highlightSimilarLines(
      textToMatchP1,
      lines,
      styleTopNodeMap,
      numberOfLinesToHighlight,
      removeGradient,
      maxLines
    );
  } else if (textToMatchP2) {
    const sentences = splitIntoSentences(textToMatchP2);
    sentences.forEach((s) =>
      highlightSimilarLines(
        s,
        lines,
        styleTopNodeMap,
        numberOfLinesToHighlight,
        removeGradient,
        maxLines
      )
    );
  }
};

// Check if we're in an email context (email-container or email-content selector)
const isEmailContext = (selector) => {
  return (
    selector &&
    (selector.includes("email-content") || selector.includes("email-container"))
  );
};

// Highlight text at the text node level for emails - preserves HTML structure
// `at`, when given, is where in the node the text sits (#12090): the ranked
// figure pass has already chosen WHICH occurrence the citation means, and a
// fresh search would wrap the first one in the node instead.
const highlightTextNodeForEmail = (
  textNode,
  textToHighlight,
  at = undefined
) => {
  try {
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return null;

    const parent = textNode.parentNode;
    if (!parent) return null;

    const content = textNode.textContent || "";
    const text = `${textToHighlight}`.trim();
    if (!text) return null;

    let startPos;
    let endPos;
    if (at === undefined) {
      // Case-insensitive search
      const regex = new RegExp(escapeRegExp(text), "i");
      const match = regex.exec(content);
      if (!match) return null;
      startPos = match.index;
      endPos = startPos + match[0].length;
    } else {
      if (content.slice(at, at + text.length) !== text) return null;
      startPos = at;
      endPos = at + text.length;
    }

    // Split the text node into three parts
    const beforeText = content.substring(0, startPos);
    const highlightText = content.substring(startPos, endPos);
    const afterText = content.substring(endPos);

    // Create text nodes
    const beforeNode = beforeText ? document.createTextNode(beforeText) : null;
    const highlightTextNode = document.createTextNode(highlightText);
    const afterNode = afterText ? document.createTextNode(afterText) : null;

    // Create highlight span
    const highlightSpan = document.createElement("span");
    highlightSpan.className = "fp-dark-highlight fp-custom-highlight";
    highlightSpan.appendChild(highlightTextNode);

    // Replace the original text node with our parts
    if (beforeNode) {
      parent.insertBefore(beforeNode, textNode);
    }
    parent.insertBefore(highlightSpan, textNode);
    if (afterNode) {
      parent.insertBefore(afterNode, textNode);
    }
    parent.removeChild(textNode);

    return highlightSpan;
  } catch (e) {
    console.error(e);
    return null;
  }
};

const highlightNode = (node, text, at = undefined) => {
  try {
    text = `${text}`.trim();
    if (!node || !text) return null;

    const content = node.textContent;
    const idx = at === undefined ? content.indexOf(text) : at;
    if (idx === -1 || content.slice(idx, idx + text.length) !== text) {
      return null;
    }

    // Preserve all whitespace - don't trim trailing whitespace as it can break email formatting
    const before = content.substring(0, idx);
    if (at === undefined && before && /\d+\.?$/.test(before.trimEnd())) {
      return null;
    }

    const highlightSpan = Object.assign(document.createElement("span"), {
      className: "fp-dark-highlight fp-custom-highlight",
      textContent: text,
    });

    // Clear and rebuild while preserving structure
    node.textContent = "";
    if (before) {
      node.append(document.createTextNode(before));
    }
    node.append(highlightSpan);
    const after = content.substring(idx + text.length);
    if (after) {
      node.append(document.createTextNode(after));
    }
    return highlightSpan;
  } catch (e) {
    console.error(e);
    return null;
  }
};

// Synchronous: every change below is a DOM edit that is done when it returns.
// It used to end on a 100 ms sleep that no caller awaited (#12187).
export const removeNodeHighlights = (selector = ".textLayer span") => {
  const container = document.querySelector(selector);
  if (!container) return;

  // Check if this is an email context
  const isEmail = isEmailContext(selector);

  if (isEmail) {
    // For emails, remove highlight spans from anywhere in the container
    container.querySelectorAll("span.fp-custom-highlight").forEach((span) => {
      const textNode = document.createTextNode(span.textContent);
      span.replaceWith(textNode);
    });
    // Remove highlight classes from elements
    container
      .querySelectorAll(
        ".fp-dark-highlight, .fp-medium-highlight, .fp-light-highlight"
      )
      .forEach((el) => {
        el.classList.remove(
          "fp-dark-highlight",
          "fp-medium-highlight",
          "fp-light-highlight"
        );
      });
    container.normalize();
  } else {
    // Original logic for non-email contexts
    const textNodes = getNodesWithDirectText(selector);
    for (const node of textNodes) {
      node.classList.remove(
        "fp-dark-highlight",
        "fp-medium-highlight",
        "fp-light-highlight"
      );
      // Find all highlight spans and replace them with text nodes to preserve structure
      const highlightSpans = node.querySelectorAll("span.fp-custom-highlight");
      highlightSpans.forEach((span) => {
        // Replace span with a text node containing its text content
        const textNode = document.createTextNode(span.textContent);
        span.replaceWith(textNode);
      });
      // Normalize to merge adjacent text nodes
      if (node.parentNode) {
        node.parentNode.normalize();
      }
      node.normalize();
    }
  }
};

function getNodesWithDirectText(selector) {
  const root = document.querySelector(selector);
  if (!root) return [];

  const found = [];
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (node.nodeType === Node.TEXT_NODE)
          return node.textContent.trim()
            ? NodeFilter.FILTER_SKIP
            : NodeFilter.FILTER_REJECT;

        const hasTextChild = [...node.childNodes].some(
          (c) => c.nodeType === Node.TEXT_NODE && c.textContent.trim()
        );
        return hasTextChild ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      },
    }
  );
  for (let n; (n = walker.nextNode()); ) found.push(n);
  return found;
}

// Get all text nodes in a container (for email highlighting)
function getAllTextNodes(container) {
  const textNodes = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      // Skip empty text nodes
      if (!node.textContent || !node.textContent.trim()) {
        return NodeFilter.FILTER_REJECT;
      }
      // Skip text nodes inside script, style, etc.
      const parent = node.parentElement;
      if (parent) {
        const tagName = parent.tagName.toLowerCase();
        if (["script", "style", "noscript"].includes(tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  while ((node = walker.nextNode())) {
    textNodes.push(node);
  }
  return textNodes;
}

/**
 * Highlight the span `[hit.index, hit.index + hit.length)` of the page text
 * the nodes were joined into (one space between nodes, as everywhere else
 * here), wrapping only the matched characters inside each node it crosses.
 * The offset inside each node is handed to the wrapper (`highlightNode`, or
 * the email body's), so a run holding the figure twice wraps the one the
 * anchoring chose. Returns whether anything was highlighted.
 */
const highlightHit = (textNodes, hit, wrap = highlightNode) => {
  let offset = 0;
  let first = null;
  let highlighted = false;
  const end = hit.index + hit.length;
  for (let i = 0; i < textNodes.length; i++) {
    if (i) offset += 1;
    const node = textNodes[i];
    const content = node.textContent || "";
    const nodeEnd = offset + content.length;
    if (offset < end && nodeEnd > hit.index) {
      const from = Math.max(hit.index, offset) - offset;
      const to = Math.min(end, nodeEnd) - offset;
      const piece = content.slice(from, to);
      // A piece that begins with whitespace (a group split across runs) is
      // wrapped from its first character, so the offset stays right.
      const lead = piece.length - piece.trimStart().length;
      const span = piece.trim() ? wrap(node, piece.trim(), from + lead) : null;
      if (span) {
        highlighted = true;
        // A wrapper that replaces the node (the email one) leaves the span
        // as the only thing on the page to scroll to.
        first ??= node.isConnected ? node : span;
      }
    }
    offset = nodeEnd;
  }
  if (first) scrollToNode(first);
  return highlighted;
};

/**
 * Highlight every node the span `[idx, idx + length)` of the joined page
 * text touches, whole, and scroll to the first — the run-level fallback the
 * cross-node passes share.
 */
const highlightRun = (textNodes, idx, length) => {
  const nodeIndexPairs = [];
  let run = "";
  textNodes.forEach((n, i) => {
    if (i) run += " ";
    nodeIndexPairs.push({ node: n, index: run.length });
    run += n.textContent;
  });

  const endIdx = idx + length;
  nodeIndexPairs.forEach(({ node, index }) => {
    if (index < endIdx && index + node.textContent.length > idx) {
      highlightNode(node, node.textContent);
    }
  });

  const target =
    nodeIndexPairs.find((p) => p.index >= idx)?.node ?? textNodes[0];
  scrollToNode(target);
};

export const highlightTextNodes = (
  selector = ".textLayer span",
  fileViewerData
) => {
  // Skip highlighting if highlightContext is an empty string, null, undefined, or only whitespace
  const context = fileViewerData?.highlightContext;
  if (!context || context === "" || context.trim() === "") {
    return;
  }

  // Check if this is an email context - use different highlighting approach
  const isEmail = isEmailContext(selector);

  if (isEmail) {
    // Email-specific highlighting that preserves HTML structure
    const container = document.querySelector(selector);
    if (!container) return;

    // Remove existing highlights
    container.querySelectorAll("span.fp-custom-highlight").forEach((span) => {
      const textNode = document.createTextNode(span.textContent);
      span.replaceWith(textNode);
    });
    container.normalize();

    const phraseToMatch =
      fileViewerData?.originalNumValue?.toString()?.trim() ||
      fileViewerData?.parentElementText?.toString()?.trim();

    if (phraseToMatch) {
      const textNodes = getAllTextNodes(container);
      const scrollToHighlight = () => {
        const highlightElement = container.querySelector(
          "span.fp-custom-highlight"
        );
        if (highlightElement) scrollToNode(highlightElement);
      };

      // A figure matches the page's spelling of it (#12090), with the same
      // ranking and anchoring the other viewers use — the first node holding
      // ANY spelling would let "page 20 of 44" beat a cited "20.0%".
      const figure = figureRegex(phraseToMatch);
      if (figure) {
        const pageText = textNodes
          .map((n) => n.textContent)
          .join(" ")
          .toLowerCase();
        const pageContext = `${context}`.toLowerCase();
        for (const tier of rankFigureHits(
          figureHits(figure, pageText),
          phraseToMatch
        )) {
          const hit = chooseAnchoredHit({
            pageText,
            hits: tier,
            context: pageContext,
          });
          if (!hit) continue;
          // An email's markup can split a number across inline elements
          // ("12<b>,082M</b>"), so the hit may cross nodes; the shared walk
          // wraps each piece at its offset.
          if (highlightHit(textNodes, hit, highlightTextNodeForEmail)) return;
        }
      } else {
        const regex = new RegExp(escapeRegExp(phraseToMatch), "i");
        for (const textNode of textNodes) {
          const content = textNode.textContent || "";
          if (regex.test(content)) {
            if (highlightTextNodeForEmail(textNode, phraseToMatch)) {
              scrollToHighlight();
              return;
            }
          }
        }
      }

      // If no direct match, try fuzzy highlighting by adding classes to parent elements
      // — unless the citation wants the value or nothing (#12090; the text
      // layer path below says why).
      if (fileViewerData?.exactOnly) return;
      applyFuzzyHighlight2(
        getNodesWithDirectText(selector),
        fileViewerData?.parentElementText,
        fileViewerData?.highlightContext,
        fileViewerData?.numberOfLinesToHighlight,
        fileViewerData?.removeGradient,
        fileViewerData?.maxHighlightLines
      );
    } else {
      if (fileViewerData?.exactOnly) return;
      // Use fuzzy highlighting for context
      applyFuzzyHighlight2(
        getNodesWithDirectText(selector),
        fileViewerData?.parentElementText,
        fileViewerData?.highlightContext,
        fileViewerData?.numberOfLinesToHighlight,
        fileViewerData?.removeGradient,
        fileViewerData?.maxHighlightLines
      );
    }
    return;
  }

  // Original highlighting logic for non-email contexts
  const textNodes = getNodesWithDirectText(selector);
  if (!textNodes.length) return;

  removeNodeHighlights(selector);

  const phraseToMatch =
    fileViewerData?.originalNumValue?.toString()?.trim() ||
    fileViewerData?.parentElementText?.toString()?.trim();

  if (phraseToMatch) {
    const figure = figureRegex(phraseToMatch);
    if (figure) {
      // A figure matches the page's spelling of the same number (#12090): a
      // table cell holds "12082" where the page prints "12,082M", a reply
      // says "2.81" against a printed "+2.81%". The pattern also holds the
      // phrase's literal spelling, bounded so the digits never match inside
      // a longer number, so it stands in for the plain substring pass below
      // — which would find "12082" inside "120820". Among the page's hits
      // the phrase's own spelling ranks first and a hit carrying its unit or
      // sign next, so "20.0%" finds "20.0%" or "20%" before the "20" in
      // "page 20 of 44"; within a rank the passage window decides, as the
      // plain pass's anchoring does, plus "nearest after the passage" for a
      // passage that locates without holding the figure (a table's caption).
      const pageText = textNodes
        .map((n) => n.textContent)
        .join(" ")
        .toLowerCase();
      const pageContext = `${context}`.toLowerCase();
      for (const tier of rankFigureHits(
        figureHits(figure, pageText),
        phraseToMatch
      )) {
        const hit = chooseAnchoredHit({
          pageText,
          hits: tier,
          context: pageContext,
        });
        if (!hit) continue;
        if (highlightHit(textNodes, hit)) return;
        // The figure is on the page but its run could not be wrapped: the
        // run-level highlight the plain pass falls back to, at the chosen hit.
        highlightRun(textNodes, hit.index, hit.length);
        return;
      }
      // Not on the page in any spelling: the passage highlight, as before.
    } else {
      const phraseLower = phraseToMatch.toLowerCase();
      // Cross-node view of the page, built up front because the single-node pass
      // below needs it too: which occurrence of the figure the citation MEANS is
      // decided against the whole page and the cited passage, not per node
      // (#11237).
      const fullString = textNodes.map((n) => n.textContent).join(" ");
      const fullLower = fullString.toLowerCase();
      const anchoredIdx = chooseAnchoredOccurrence({
        pageText: fullLower,
        phrase: phraseLower,
        context: `${context}`.toLowerCase(),
      });

      // Single-node, case-insensitive. Nodes are visited in page order but
      // STARTING FROM the node holding the anchored occurrence, so a figure that
      // also appears in a header or an unrelated table earlier on the page no
      // longer wins the highlight just by being first (#11237). Nodes before the
      // anchor still get their turn after it, which keeps this a preference
      // rather than a filter: if the anchored node can't be highlighted for any
      // reason, the old first-match result is still reachable.
      const regex = new RegExp(escapeRegExp(phraseToMatch), "i");
      for (const node of orderedFromAnchor(textNodes, anchoredIdx)) {
        const content = node.textContent;
        const m = content.match(regex);
        if (m) {
          if (highlightNode(node, m[0])) {
            scrollToNode(node);
            return;
          }
        }
      }

      let idx = anchoredIdx;
      let matchedLength = phraseLower.length;

      // Enumerate space variants for single word phrases that didn't match, e.g. if "1.9x" was represented as "1. 9 x".
      if (idx === -1 && !phraseLower.includes(" ")) {
        const variants = enumerateSpaceVariants(phraseLower);
        for (const variant of variants) {
          const variantIdx = fullLower.indexOf(variant);
          if (variantIdx !== -1) {
            idx = variantIdx;
            matchedLength = variant.length;
            break;
          }
        }
      }

      if (idx !== -1) {
        highlightRun(textNodes, idx, matchedLength);
        return;
      }
    }
  }

  // A table cell asks for the value where the page prints it, or nothing
  // (#12090): its passage is the whole source table, and a value the agent
  // reworded, converted or computed is on no page at all, so the passage
  // highlight below would tint every row. The previous highlight has been
  // cleared, and the viewer opened where its own scroll rules put it (a
  // note or post opens at its top); that is the whole result for such a
  // cell.
  if (fileViewerData?.exactOnly) return;

  applyFuzzyHighlight2(
    textNodes,
    fileViewerData?.parentElementText,
    fileViewerData?.highlightContext,
    fileViewerData?.numberOfLinesToHighlight,
    fileViewerData?.removeGradient,
    fileViewerData?.maxHighlightLines
  );
};
